<template>
  <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm mt-8">
    <!-- Header -->
    <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 pb-6">
      <div class="leading-none font-semibold text-lg">Company Profile</div>
      <div class="text-muted-foreground text-sm">
        {{ showKeyProjects ? 'Share details about your operations and key projects' : 'Share details about your team and experience' }}
      </div>
    </div>

    <!-- Content -->
    <div class="px-6 space-y-8">
      <!-- Key People -->
      <div>
        <div class="flex items-center gap-2 mb-2">
          <label class="font-medium text-sm">Key People</label>
          <span class="text-muted-foreground text-xs">(executives, managers, etc.)</span>
        </div>
        <div class="space-y-3">
          <div
            v-for="(person, index) in localData.keyPeople"
            :key="index"
            class="rounded-lg border border-border/60 bg-background/80 p-3 shadow-sm"
          >
            <div class="grid gap-2 sm:grid-cols-[repeat(4,minmax(0,1fr))] sm:items-start">
              <input
                v-model="person.firstName"
                type="text"
                placeholder="First name"
                class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              />
              <input
                v-model="person.lastName"
                type="text"
                placeholder="Last name"
                class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              />
              <input
                v-model="person.role"
                type="text"
                placeholder="Role / Job Title"
                class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none sm:col-span-1"
              />
              <VueDatePicker
                v-model="person.roleStartDate"
                :enable-time-picker="false"
                auto-apply
                teleport
                model-type="yyyy-MM-dd"
                format="dd/MM/yyyy"
                placeholder="Role start date"
                :input-class="[
                  'w-full h-10 rounded-md border border-border text-foreground px-3 text-sm focus:ring-2 focus:ring-brand focus:border-brand bg-background',
                ]"
                :menu-class="['rounded-lg shadow-lg border border-border bg-background']"
              />
            </div>
            <div
              v-if="localData.keyPeople.length > 1"
              class="mt-3 flex justify-end"
            >
              <button
                @click="removeKeyPerson(index)"
                class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:border-brand hover:text-foreground"
                type="button"
              >
                Remove
              </button>
            </div>
          </div>

          <button
            type="button"
            @click="addKeyPerson"
            class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand"
          >
            + Add Person
          </button>
        </div>
        <p v-if="errors.keyPeople" class="text-sm text-red-600 mt-1">{{ errors.keyPeople }}</p>
      </div>

      <!-- Commodities hidden for now; keep the markup nearby for easy recovery. -->
      <div v-if="false && shouldShowCommodities">
        <label class="font-medium text-sm mb-2 block">Commodities</label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="commodity in COMMODITIES"
            :key="commodity"
            type="button"
            @click="toggleCommodity(commodity)"
            class="px-3 py-1 rounded-full border text-sm transition-colors"
            :class="localData.commodities.includes(commodity)
              ? 'bg-brand text-white border-brand'
              : 'border-muted-foreground/30 text-muted-foreground hover:border-brand hover:text-foreground'"
          >
            {{ commodity }}
          </button>
        </div>
        <p v-if="errors.commodities" class="text-sm text-red-600 mt-1">{{ errors.commodities }}</p>
      </div>

      <!-- Industry-specific fields (e.g. construction sectors) -->
      <IndustryAttributeFields
        :profile="industryProfile"
        v-model="localData.industryAttributes"
        :errors="industryAttributeFieldErrors"
      />

      <!-- Key Projects -->
      <div v-if="showKeyProjects">
        <label class="font-medium text-sm mb-2 block">Key Projects</label>
        <div class="space-y-3">
          <div
            v-for="(project, index) in localData.keyProjects"
            :key="index"
            class="rounded-lg border border-border/60 bg-background/80 p-3 shadow-sm space-y-2"
          >
            <div class="flex flex-col sm:flex-row gap-2">
              <input
                v-model="project.name"
                type="text"
                placeholder="Project name"
                class="flex-1 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              />
              <select
                v-model="project.stage"
                class="w-full sm:w-48 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              >
                <option value="">Stage</option>
                <option v-for="stage in PROJECT_STAGES" :key="stage" :value="stage">{{ stage }}</option>
              </select>
              <button
                v-if="localData.keyProjects.length > 1"
                @click="removeKeyProject(index)"
                class="border border-border rounded-md px-2 hover:bg-muted"
                type="button"
              >
                ✕
              </button>
            </div>

            <input
              v-model="project.location_label"
              type="text"
              placeholder="Project location"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            />

            <textarea
              v-model="project.description"
              rows="3"
              placeholder="Project description"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            ></textarea>

            <GoogleMap
              v-if="project.location_label"
              editable
              :address="project.location_label"
              :lat="project.location_lat ?? null"
              :lng="project.location_lng ?? null"
              map-height-class="h-40"
              @update:location="updateProjectLocation(index, $event)"
            />

            <div class="space-y-2 rounded-md border border-border/60 bg-white/70 p-3">
              <div class="flex items-center justify-between gap-2">
                <p class="text-xs font-medium text-muted-foreground">Activity feed</p>
                <button
                  type="button"
                  class="text-xs font-semibold text-brand hover:text-brand/80"
                  @click="addProjectActivity(index)"
                >
                  + Add update
                </button>
              </div>

              <div
                v-for="(activity, activityIndex) in project.activity_feed"
                :key="activityIndex"
                class="grid gap-2 sm:grid-cols-[150px,minmax(0,1fr),auto]"
              >
                <input
                  v-model="activity.date"
                  type="date"
                  class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <textarea
                  v-model="activity.text"
                  rows="2"
                  placeholder="Market update"
                  class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                ></textarea>
                <button
                  type="button"
                  class="border border-border rounded-md px-2 hover:bg-muted"
                  @click="removeProjectActivity(index, activityIndex)"
                >
                  ✕
                </button>
              </div>
            </div>

            <p class="text-xs font-medium text-muted-foreground">Project commodities</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="commodity in COMMODITIES"
                :key="commodity"
                type="button"
                @click="toggleProjectCommodity(index, commodity)"
                class="px-3 py-1 rounded-full border text-sm transition-colors"
                :class="project.commodities?.includes(commodity)
                  ? 'bg-brand text-white border-brand'
                  : 'border-muted-foreground/30 text-muted-foreground hover:border-brand hover:text-foreground'"
              >
                {{ commodity }}
              </button>
            </div>
          </div>

          <button
            type="button"
            @click="addKeyProject"
            class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand"
          >
            + Add Project
          </button>
        </div>
      </div>

      <!-- Market Data -->
      <div
        v-if="shouldShowMarketData"
        class="md:col-span-2"
      >
        <label class="block text-sm font-medium mb-3">Market Data</label>

        <div
          class="flex flex-col gap-4 border border-border rounded-lg p-4 bg-muted/10"
        >
          <div class="flex items-center justify-between gap-4">
            <div>
              <p class="text-sm font-medium text-foreground">Private company</p>
              <p class="text-xs text-muted-foreground">
                Select this if your company is not publicly listed.
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="localData.isPrivateCompany"
                type="checkbox"
                class="sr-only peer"
              />
              <div class="h-6 w-11 rounded-full border border-border bg-muted/40 transition-colors peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40"></div>
              <div class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
            </label>
          </div>

          <!-- ASX Ticker -->
          <div class="w-full">
            <label class="block text-xs font-medium mb-1 text-muted-foreground">ASX Ticker</label>
            <div class="relative">
              <input
                v-model="localData.companyTicker"
                type="text"
                placeholder="e.g., BHP"
                maxlength="5"
                :disabled="localData.isPrivateCompany"
                @input="onTickerInput"
                class="w-full border border-border rounded-md px-3 py-2 uppercase focus:ring-2 focus:ring-brand focus:border-transparent outline-none disabled:bg-muted/40 disabled:text-muted-foreground"
              />
              <svg
                v-if="isFetchingMarketCap"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-brand"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M4 12a8 8 0 018-8"></path>
              </svg>
            </div>

            <p v-if="marketCapError" class="text-xs text-red-600 mt-1">{{ marketCapError }}</p>
            <p v-else class="text-xs text-muted-foreground mt-1">
              Market cap is fetched automatically from the ASX ticker. If you’re not listed, mark the company as private.
            </p>
          </div>

          <div v-if="localData.marketCap" class="w-full">
            <label class="block text-xs font-medium mb-1 text-muted-foreground">Market Cap (auto)</label>
            <div class="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
              {{ localData.marketCap }}
            </div>
          </div>
        </div>
      </div>

      <!-- Cash in Bank -->
      <div
        v-if="shouldShowMarketData"
        class="md:col-span-2"
      >
        <label class="block text-sm font-medium mb-1">Cash in Bank</label>
        <input
          v-model="localData.cashInBank"
          type="text"
          placeholder="e.g., $10M AUD"
          @input="onCashInBankInput"
          class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
        />
        <div v-if="toSafeString(localData.cashInBank)" class="mt-3">
          <label class="block text-xs font-medium mb-1 text-muted-foreground">Date of Balance</label>
          <VueDatePicker
            v-model="localData.cashInBankUpdatedAt"
            :enable-time-picker="false"
            auto-apply
            teleport
            model-type="yyyy-MM-dd"
            format="dd/MM/yyyy"
            placeholder="Select date"
            :input-class="[
              'w-full h-10 rounded-md border border-border text-foreground px-3 text-sm focus:ring-2 focus:ring-brand focus:border-brand bg-background',
            ]"
            :menu-class="['rounded-lg shadow-lg border border-border bg-background']"
          />
          <p v-if="errors.cashInBankDate" class="text-sm text-red-600 mt-1">{{ errors.cashInBankDate }}</p>
        </div>
      </div>

      <!-- Cash Runway -->
      <div
        v-if="shouldShowMarketData"
        class="md:col-span-2"
      >
        <label class="block text-sm font-medium mb-1">Cash Runway</label>
        <input
          v-model="localData.cashRunway"
          type="text"
          inputmode="decimal"
          placeholder="e.g., 4 or 4.5"
          @input="onCashRunwayInput"
          class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
        />
        <p v-if="errors.cashRunway" class="text-sm text-red-600 mt-1">{{ errors.cashRunway }}</p>
        <p class="mt-1 text-xs text-muted-foreground">
          Estimated time (in quarters) a company can operate using current cash, based on recent spend
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center px-6 pt-6">
      <button
        class="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"
        @click="$emit('back')"
      >
        Back
      </button>
      <button
        class="px-4 py-2 rounded-md bg-brand text-white text-sm hover:opacity-90"
        @click="validateAndProceed"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useCompanyMarketData } from '@/composables/useCompanyMarketData'
import { getCompanyMarketCap as getRegistrationMarketCap } from '@/api/company/registration'
import GoogleMap from '@/components/marketplace/generic/GoogleMap.vue'
import IndustryAttributeFields from '@/components/marketplace/company/IndustryAttributeFields.vue'
import { useCompanyIndustryProfileStore } from '@/stores/companyIndustryProfileStore'
import { industryAttributeErrors } from '@/validation/industryAttributes'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

const props = defineProps<{
  data: Record<string, any>
  industries?: Array<{ id: number; name: string }>
}>()

const emits = defineEmits(['update', 'next', 'back'])

const toSafeString = (value: unknown): string => (typeof value === 'string' ? value : '').trim()
const isDecimalString = (value: string): boolean => /^\d+(?:\.\d+)?$/.test(value)
const normalizeDecimalInput = (value: unknown): string => {
  const raw = String(value ?? '').replace(/[^\d.]/g, '')
  const [whole = '', ...decimalParts] = raw.split('.')
  if (decimalParts.length === 0) return whole
  return `${whole}.${decimalParts.join('')}`
}
const splitLegacyName = (value: unknown) => {
  const fullName = toSafeString(value)
  if (!fullName) return { firstName: '', lastName: '' }
  const parts = fullName.split(/\s+/)
  const firstName = parts.shift() ?? ''
  const lastName = parts.join(' ')
  return { firstName, lastName }
}

const normalizeCommoditiesList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => toSafeString(item)).filter((item) => item !== '')
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
  }
  return []
}

const normalizeKeyPeople = (value: unknown) => {
  if (!Array.isArray(value) || value.length === 0) {
    return [{ firstName: '', lastName: '', role: '', roleStartDate: '' }]
  }

  return value.map((entry: any) => {
    const firstName = toSafeString(entry?.firstName)
    const lastName = toSafeString(entry?.lastName)
    const role = toSafeString(entry?.role ?? entry?.position)
    const roleStartDate = toSafeString(entry?.roleStartDate ?? entry?.role_start_date ?? entry?.startDate ?? entry?.start_date)

    if (firstName || lastName) {
      return { firstName, lastName, role, roleStartDate }
    }

    const legacy = splitLegacyName(entry?.name)
    return { firstName: legacy.firstName, lastName: legacy.lastName, role, roleStartDate }
  })
}

const normalizeKeyProjects = (value: unknown) => {
  if (!Array.isArray(value) || value.length === 0) {
    return [{
      name: '',
      stage: '',
      description: '',
      location_label: '',
      location_lat: null,
      location_lng: null,
      commodities: [],
      activity_feed: [],
    }]
  }

  return value.map((entry: any) => ({
    name: toSafeString(entry?.name),
    stage: toSafeString(entry?.stage),
    description: toSafeString(entry?.description),
    location_label: toSafeString(entry?.location_label ?? entry?.locationLabel ?? entry?.location),
    location_lat: entry?.location_lat ?? entry?.locationLat ?? entry?.lat ?? null,
    location_lng: entry?.location_lng ?? entry?.locationLng ?? entry?.lng ?? entry?.lon ?? null,
    commodities: normalizeCommoditiesList(entry?.commodities),
    activity_feed: Array.isArray(entry?.activity_feed ?? entry?.activityFeed)
      ? (entry?.activity_feed ?? entry?.activityFeed)
          .map((activity: any) => ({
            date: toSafeString(activity?.date ?? activity?.released_at ?? activity?.releasedAt),
            text: toSafeString(activity?.text ?? activity?.update ?? activity?.description),
          }))
          .filter((activity: any) => activity.date || activity.text)
      : [],
  }))
}

const localData = ref({
  ...props.data,
  keyPeople: normalizeKeyPeople((props.data as any).keyPeople),
  keyProjects: normalizeKeyProjects((props.data as any).keyProjects),
  industryAttributes: { ...((props.data as any).industryAttributes ?? {}) },
  isPrivateCompany: Boolean((props.data as any).isPrivateCompany),
  cashInBankUpdatedAt:
    (props.data as any).cashInBankUpdatedAt ?? (props.data as any).cashInBankRecordedAt ?? null,
})
const errors = ref<Record<string, string>>({})

const tickerRef = toRef(localData.value, 'companyTicker')
const marketCapRef = toRef(localData.value, 'marketCap')

const {
  isFetching: isFetchingMarketCap,
  error: marketCapError,
  onTickerInput,
  onMarketCapInput,
} = useCompanyMarketData(tickerRef, marketCapRef, {
  fetcher: async (symbol) => {
    try {
      const { data } = await getRegistrationMarketCap(symbol)
      return data
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return { notFound: true }
      }
      throw err
    }
  },
})

const COMMODITIES = [
  'Aluminium',
  'Ammonia',
  'Antimony',
  'Arsenic',
  'Barium',
  'Bauxite',
  'Beryllium',
  'Bismuth',
  'Boron',
  'Caesium',
  'Calcium Carbonate',
  'Chromite',
  'Chromium',
  'Clinker',
  'Coal',
  'Cobalt',
  'Copper',
  'Diamonds',
  'Dolerite',
  'Dysprosium',
  'Feldspar',
  'Fluorine',
  'Fluorspar',
  'Gallium',
  'Garnet',
  'Germanium',
  'Gold',
  'Graphite',
  'Granite',
  'Gypsum',
  'Hafnium',
  'Halloysite',
  'Helium',
  'Hydrocarbon',
  'Hydrogen',
  'Ilmenite',
  'Indium',
  'Iridium',
  'Iron Ore',
  'Kaolin',
  'Lead',
  'Lepidolite',
  'Leucoxene',
  'Limestone',
  'Lithium',
  'LNG',
  'Magnesium',
  'Manganese',
  'Mineral Sands',
  'Monazite',
  'Molybdenum',
  'Muscovite',
  'Neodymium',
  'Nickel',
  'Niobium',
  'Nitrogen',
  'Osmium',
  'Palladium',
  'Petroleum',
  'Phosphate',
  'Phosphorus',
  'Platinum',
  'Polymetallic Nodule',
  'Potash',
  'Potassium',
  'Praseodymium',
  'Quartz',
  'Rare Earth Elements',
  'Rhenium',
  'Rhodium',
  'Rubidium',
  'Ruthenium',
  'Rutile',
  'Scandium',
  'Silica Sand',
  'Silicon',
  'Silver',
  'Sodium Chloride',
  'Strontium',
  'Sulphur',
  'Tantalite',
  'Tantalum',
  'Terbium',
  'Tellurium',
  'Thorium',
  'Tin',
  'Titanium',
  'Topaz',
  'Tungsten',
  'Uranium',
  'Urea',
  'Vanadium',
  'Volcanogenic Massive Sulfide',
  'Xenotime',
  'Zinc',
  'Zircon',
  'Zirconium',
  'Other',
]

const PROJECT_STAGES = [
  'Exploration',
  'Pre-Feasibility',
  'Feasibility',
  'Development',
  'Production',
  'Brownfield',
  'Care & Maintenance',
  'Rehabilitation and Closure'
]

const resolvedIndustryName = computed(() => {
  const industries = props.industries ?? []
  const rawValue = localData.value.industry

  if (rawValue == null || rawValue === '') return ''

  const asNumber = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  if (!Number.isNaN(asNumber)) {
    const matchById = industries.find((industry) => industry.id === asNumber)
    if (matchById?.name) return matchById.name
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim()
    if (!trimmed) return ''

    const lower = trimmed.toLowerCase()
    const matchByName = industries.find((industry) => industry.name?.toLowerCase() === lower)
    if (matchByName?.name) return matchByName.name

    return trimmed
  }

  return ''
})

const shouldShowCommodities = computed(() => {
  const name = resolvedIndustryName.value.trim().toLowerCase()
  return name !== '' && name.includes('mining')
})

const industryProfileStore = useCompanyIndustryProfileStore()
const industryProfile = computed(() => industryProfileStore.resolve(resolvedIndustryName.value))

const shouldShowMarketData = computed(() => industryProfile.value?.sections.market_data ?? false)
const showKeyProjects = computed(() => industryProfile.value?.sections.key_projects ?? true)

const industryAttributeFieldErrors = computed(() => {
  const fieldErrors: Record<string, string> = {}
  for (const [key, message] of Object.entries(errors.value)) {
    if (key.startsWith('industryAttributes.')) fieldErrors[key.slice('industryAttributes.'.length)] = message
  }
  return fieldErrors
})

watch(localData, (val) => emits('update', val), { deep: true })

watch(
  () => localData.value.isPrivateCompany,
  (isPrivate) => {
    if (!isPrivate) return
    localData.value.companyTicker = ''
    localData.value.marketCap = ''
  }
)

watch(shouldShowCommodities, (show) => {
  if (show) return
  const nextErrors = { ...errors.value }
  delete nextErrors.commodities
  errors.value = nextErrors
})

watch(shouldShowMarketData, (show) => {
  if (show) return
  const nextErrors = { ...errors.value }
  delete nextErrors.marketData
  delete nextErrors.cashInBank
  delete nextErrors.cashInBankDate
  delete nextErrors.cashRunway
  errors.value = nextErrors
})

const addKeyPerson = () => localData.value.keyPeople.push({ firstName: '', lastName: '', role: '', roleStartDate: '' })
const removeKeyPerson = (index: number) => {
  localData.value.keyPeople.splice(index, 1)
  if (!localData.value.keyPeople.length)
    localData.value.keyPeople.push({ firstName: '', lastName: '', role: '', roleStartDate: '' })
}

const addKeyProject = () => localData.value.keyProjects.push({
  name: '',
  stage: '',
  description: '',
  location_label: '',
  location_lat: null,
  location_lng: null,
  commodities: [],
  activity_feed: [],
})
const removeKeyProject = (index: number) => {
  localData.value.keyProjects.splice(index, 1)
  if (!localData.value.keyProjects.length) {
    localData.value.keyProjects.push({
      name: '',
      stage: '',
      description: '',
      location_label: '',
      location_lat: null,
      location_lng: null,
      commodities: [],
      activity_feed: [],
    })
  }
}

const toggleProjectCommodity = (index: number, commodity: string) => {
  const project = localData.value.keyProjects[index]
  if (!project) return
  if (!Array.isArray(project.commodities)) {
    project.commodities = []
  }
  if (project.commodities.includes(commodity)) {
    project.commodities = project.commodities.filter((item: string) => item !== commodity)
  } else {
    project.commodities.push(commodity)
  }
}

const updateProjectLocation = (index: number, location: { lat: number; lng: number }) => {
  const project = localData.value.keyProjects[index]
  if (!project) return
  project.location_lat = location.lat
  project.location_lng = location.lng
}

const addProjectActivity = (index: number) => {
  const project = localData.value.keyProjects[index]
  if (!project) return
  if (!Array.isArray(project.activity_feed)) {
    project.activity_feed = []
  }
  project.activity_feed.push({ date: '', text: '' })
}

const removeProjectActivity = (projectIndex: number, activityIndex: number) => {
  const project = localData.value.keyProjects[projectIndex]
  if (!project || !Array.isArray(project.activity_feed)) return
  project.activity_feed.splice(activityIndex, 1)
}

const toggleCommodity = (commodity: string) => {
  if (!Array.isArray(localData.value.commodities)) {
    localData.value.commodities = []
  }
  const list = localData.value.commodities
  if (list.includes(commodity)) {
    localData.value.commodities = list.filter((c: string) => c !== commodity)
  } else {
    localData.value.commodities.push(commodity)
  }
}

const onCashInBankInput = () => {
  localData.value.cashInBankUpdatedAt = null
}

const onCashRunwayInput = () => {
  localData.value.cashRunway = normalizeDecimalInput(localData.value.cashRunway)
}

const validateAndProceed = () => {
  const newErrors: Record<string, string> = {}

  const hasCompletePerson = localData.value.keyPeople.some(
    (p: any) => toSafeString(p.firstName) && toSafeString(p.lastName) && toSafeString(p.role)
  )
  if (!hasCompletePerson) {
    newErrors.keyPeople = 'Add at least one key person with first name, last name, and role'
  }

  for (const [key, message] of Object.entries(
    industryAttributeErrors(industryProfile.value, localData.value.industryAttributes)
  )) {
    newErrors[`industryAttributes.${key}`] = message
  }

  const hasMarketData = toSafeString(localData.value.companyTicker)
  if (shouldShowCommodities.value && !localData.value.isPrivateCompany && !hasMarketData) {
    newErrors.marketData = 'Enter an exchange ticker or mark the company as private'
  }

  if (toSafeString(localData.value.cashInBank) && !toSafeString(localData.value.cashInBankUpdatedAt)) {
    newErrors.cashInBankDate = 'Date of Balance is required when cash in bank is provided'
  }

  const cashRunway = toSafeString(localData.value.cashRunway)
  if (cashRunway && !isDecimalString(cashRunway)) {
    newErrors.cashRunway = 'Cash runway must be a number or decimal'
  }

  errors.value = newErrors

  if (Object.keys(newErrors).length === 0) emits('next')
}
</script>
